import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { app, authentication } from '@microsoft/teams-js';

interface TeamsContextValue {
  isInTeams: boolean;
  teamsContext: any | null;
  isInitialized: boolean;
  theme: string | null;
  getTeamsAuthToken: () => Promise<string | null>;
}

const TeamsContext = createContext<TeamsContextValue>({
  isInTeams: false,
  teamsContext: null,
  isInitialized: false,
  theme: null,
  getTeamsAuthToken: async () => null,
});

interface TeamsProviderProps {
  children: ReactNode;
}

export const TeamsProvider: React.FC<TeamsProviderProps> = ({ children }) => {
  const [isInTeams, setIsInTeams] = useState<boolean>(false);
  const [teamsContext, setTeamsContext] = useState<any | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [theme, setTheme] = useState<string | null>(null);

  const getTeamsAuthToken = async (): Promise<string | null> => {
    if (!isInTeams || !isInitialized) {
      return null;
    }

    try {
      // Use the modern Teams JS SDK v2.x API
      const token = await authentication.getAuthToken();
      return token;
    } catch (error) {
      console.error('Teams SSO failed:', error instanceof Error ? error.message : error);
      return null;
    }
  };

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        // Check for manual Teams simulation via URL parameter
        const urlParams = new URLSearchParams(window.location.search);
        const teamsParam = urlParams.get('teams');
        const themeParam = urlParams.get('theme');

        // Force Teams mode for testing only when explicitly requested
        if (teamsParam === 'true') {
          // Try to initialize Teams SDK
          try {
            await app.initialize();
            setIsInitialized(true);

            const context = await app.getContext();
            if (context && context.app?.host?.name) {
              // Real Teams context
              setIsInTeams(true);
              setTeamsContext(context);
              setTheme(context.app.theme || themeParam || 'default');

              app.registerOnThemeChangeHandler((theme: string) => {
                setTheme(theme);
              });
            } else {
              // Simulated Teams context for local testing
              setIsInTeams(true);
              setTeamsContext({
                app: {
                  host: { name: 'Teams' },
                  theme: themeParam || 'default'
                },
                user: {
                  id: 'test-user',
                  displayName: 'Test User (Local)',
                  loginHint: 'testuser@contoso.com'
                }
              } as any);
              setTheme(themeParam || 'default');
            }
          } catch (teamsError) {
            // Teams SDK failed, but still simulate for local testing
            setIsInTeams(true);
            setIsInitialized(true);
            setTeamsContext({
              app: {
                host: { name: 'Teams (Simulated)' },
                theme: themeParam || 'default'
              },
              user: {
                id: 'simulated-user',
                displayName: 'Simulated User (Local)',
                loginHint: 'testuser@contoso.com'
              }
            } as any);
            setTheme(themeParam || 'default');
          }
        } else {
          // Normal Teams detection - only try if we might be in Teams
          try {
            await app.initialize();
            setIsInitialized(true);

            const context = await app.getContext();
            if (context && context.app?.host?.name) {
              setIsInTeams(true);
              setTeamsContext(context);
              setTheme(context.app.theme || 'default');

              app.registerOnThemeChangeHandler((theme: string) => {
                setTheme(theme);
              });
            } else {
              // Not in Teams, just set defaults
              setIsInTeams(false);
              setIsInitialized(true);
            }
          } catch (teamsError) {
            // Not in Teams environment, this is expected for browser access
            setIsInTeams(false);
            setIsInitialized(true);
          }
        }
      } catch (error) {
        console.error('Teams initialization error:', error);
        setIsInTeams(false);
        setIsInitialized(false);
      }
    };

    initializeTeams();
  }, []);

  const value: TeamsContextValue = {
    isInTeams,
    teamsContext,
    isInitialized,
    theme,
    getTeamsAuthToken,
  };

  return <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>;
};

export const useTeams = (): TeamsContextValue => {
  const context = useContext(TeamsContext);
  if (!context) {
    throw new Error('useTeams must be used within a TeamsProvider');
  }
  return context;
};